import React from 'react';
import { Helmet } from 'react-helmet-async';

/**
 * Terms of Service page component
 */
const TermsOfService = () => {
  // Last updated date
  const lastUpdated = 'November 15, 2023';

  return (
    <>
      <Helmet>
        <title>Terms of Service | Meira</title>
        <meta name="description" content="Review Meira's Terms of Service, including user responsibilities, rules of conduct, and legal terms for using our platform." />
      </Helmet>

      <div className="bg-primary py-16">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center">
            <h1 className="text-4xl font-extrabold text-white sm:text-5xl sm:tracking-tight lg:text-6xl">Terms of Service</h1>
            <p className="mt-5 max-w-xl mx-auto text-xl text-indigo-100">
              Please read these terms carefully before using our platform
            </p>
          </div>
        </div>
      </div>

      <div className="py-16 bg-white">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="prose prose-lg prose-primary mx-auto">
            <p className="text-gray-500">Last Updated: {lastUpdated}</p>
            
            <p>
              Welcome to Meira. These Terms of Service ("Terms") govern your access to and use of the Meira platform, 
              website, and services (collectively, the "Services"). Please read these Terms carefully before using our Services.
            </p>

            <p>
              By accessing or using our Services, you agree to be bound by these Terms. If you do not agree to these Terms, 
              you may not access or use the Services.
            </p>

            <h2>1. Acceptance of Terms</h2>
            <p>
              By creating an account, accessing, or using our Services, you confirm that you are at least 13 years of age 
              (or the minimum legal age in your jurisdiction, if greater) and that you agree to be bound by these Terms. 
              If you are using the Services on behalf of an organization, you represent and warrant that you have the authority 
              to bind that organization to these Terms.
            </p>

            <h2>2. Changes to Terms</h2>
            <p>
              We may modify these Terms at any time, at our sole discretion. If we do so, we'll let you know either by posting 
              the modified Terms on our website or through other communications. It's important that you review the Terms whenever 
              we modify them because if you continue to use the Services after we have posted modified Terms, you are indicating 
              to us that you agree to be bound by the modified Terms. If you don't agree to be bound by the modified Terms, then 
              you may not use the Services anymore.
            </p>

            <h2>3. Account Registration and Security</h2>
            <p>
              To use certain features of our Services, you may need to create an account. When you register, you agree to provide 
              accurate, current, and complete information about yourself and to update this information to keep it accurate, current, 
              and complete. You are responsible for safeguarding your account credentials and for all activities that occur under your 
              account. You agree to notify us immediately of any unauthorized use of your account.
            </p>
            <p>
              We reserve the right to disable any user account at any time, including if we believe that you have violated these Terms.
            </p>

            <h2>4. User Conduct</h2>
            <p>
              You agree that you will not:
            </p>
            <ul>
              <li>Use the Services in any manner that could interfere with, disrupt, negatively affect or inhibit other users from fully enjoying the Services</li>
              <li>Use the Services in any way that violates applicable laws or regulations</li>
              <li>Use the Services to transmit or distribute material that infringes on intellectual property rights</li>
              <li>Use the Services to distribute malicious code or attempt to gain unauthorized access to any system, data, or network</li>
              <li>Impersonate any person or entity or misrepresent your affiliation with a person or entity</li>
              <li>Engage in any harassing, intimidating, predatory, or stalking conduct</li>
              <li>Use the Services to send unsolicited communications or promotions</li>
              <li>Attempt to circumvent any content-filtering techniques we employ</li>
              <li>Encourage or enable any other individual to do any of the foregoing</li>
            </ul>

            <h2>5. Educational Institution Accounts</h2>
            <p>
              If you are an educational institution using our Services, you agree to:
            </p>
            <ul>
              <li>Comply with all applicable education laws and regulations, including FERPA (Family Educational Rights and Privacy Act)</li>
              <li>Obtain all necessary parental consents for the collection and use of student information as required by COPPA (Children's Online Privacy Protection Act) and other applicable laws</li>
              <li>Use the Services only for educational purposes as authorized by your institution</li>
              <li>Ensure that your administrators, teachers, staff, and students comply with these Terms and our Privacy Policy</li>
            </ul>

            <h2>6. Content and Intellectual Property Rights</h2>
            <h3>6.1 User Content</h3>
            <p>
              Our Services allow you to create, upload, post, send, receive, and store content (collectively, "User Content"). 
              You retain all rights in, and are solely responsible for, the User Content you create, upload, post, send, receive, 
              or store on or through our Services.
            </p>
            <p>
              By submitting User Content through the Services, you grant Meira a worldwide, non-exclusive, royalty-free, sublicensable, 
              and transferable license to use, reproduce, modify, distribute, create derivative works of, display, and perform the User 
              Content in connection with operating and providing the Services.
            </p>

            <h3>6.2 Feedback</h3>
            <p>
              If you provide feedback, ideas, or suggestions regarding our Services ("Feedback"), you grant us the right to use such 
              Feedback without restriction and without compensation to you.
            </p>

            <h3>6.3 Meira Content</h3>
            <p>
              Unless otherwise indicated, the Services and all content and materials available through the Services, including our 
              logos, designs, text, graphics, images, videos, information, software, and other files (collectively, "Meira Content"), 
              are the proprietary property of Meira or our licensors or users. You may not duplicate, copy, or reuse any portion of the 
              Meira Content without express written permission from us.
            </p>

            <h2>7. Subscriptions and Payment</h2>
            <p>
              Some of our Services may require a subscription or payment. By subscribing to any paid Services, you agree to pay all 
              fees associated with your subscription. Unless otherwise stated:
            </p>
            <ul>
              <li>All fees are quoted in U.S. Dollars</li>
              <li>Payments are non-refundable</li>
              <li>You authorize us to charge your designated payment method for all fees</li>
              <li>Subscriptions automatically renew unless canceled prior to the renewal date</li>
              <li>We may change the fees for our Services by providing you with advance notice</li>
            </ul>
            <p>
              You are responsible for all taxes applicable to the fees paid by you.
            </p>

            <h2>8. Termination</h2>
            <p>
              We reserve the right to suspend or terminate your access to the Services at any time and for any reason, including if we 
              believe that you have violated these Terms. Upon termination:
            </p>
            <ul>
              <li>Your right to use the Services will immediately cease</li>
              <li>We may delete or retain your account information and User Content at our discretion</li>
              <li>Certain provisions of these Terms will survive termination, including ownership provisions, warranty disclaimers, limitations of liability, and dispute resolution</li>
            </ul>

            <h2>9. Disclaimers</h2>
            <p>
              THE SERVICES ARE PROVIDED "AS IS" AND "AS AVAILABLE" WITHOUT WARRANTIES OF ANY KIND, EITHER EXPRESS OR IMPLIED, 
              INCLUDING, BUT NOT LIMITED TO, IMPLIED WARRANTIES OF MERCHANTABILITY, FITNESS FOR A PARTICULAR PURPOSE, TITLE, AND 
              NON-INFRINGEMENT. MEIRA DOES NOT WARRANT THAT THE SERVICES WILL BE UNINTERRUPTED OR ERROR-FREE, THAT DEFECTS WILL BE 
              CORRECTED, OR THAT THE SERVICES OR THE SERVERS THAT MAKE THEM AVAILABLE ARE FREE OF VIRUSES OR OTHER HARMFUL COMPONENTS.
            </p>
            <p>
              MEIRA MAKES NO GUARANTEES REGARDING THE ACCURACY, RELIABILITY, COMPLETENESS, OR TIMELINESS OF THE CONTENT, INFORMATION, 
              SOFTWARE, TEXT, GRAPHICS, LINKS, OR COMMUNICATIONS PROVIDED ON OR THROUGH THE USE OF THE SERVICES.
            </p>
            <p>
              SOME JURISDICTIONS DO NOT ALLOW THE EXCLUSION OF IMPLIED WARRANTIES, SO THE ABOVE EXCLUSION MAY NOT APPLY TO YOU.
            </p>

            <h2>10. Limitation of Liability</h2>
            <p>
              IN NO EVENT WILL MEIRA, ITS DIRECTORS, EMPLOYEES, PARTNERS, AGENTS, SUPPLIERS, OR AFFILIATES BE LIABLE FOR ANY INDIRECT, 
              INCIDENTAL, SPECIAL, CONSEQUENTIAL, OR PUNITIVE DAMAGES, INCLUDING WITHOUT LIMITATION, LOSS OF PROFITS, DATA, USE, 
              GOODWILL, OR OTHER INTANGIBLE LOSSES, RESULTING FROM (I) YOUR ACCESS TO OR USE OF OR INABILITY TO ACCESS OR USE THE 
              SERVICES; (II) ANY CONDUCT OR CONTENT OF ANY THIRD PARTY ON THE SERVICES; (III) ANY CONTENT OBTAINED FROM THE SERVICES; 
              AND (IV) UNAUTHORIZED ACCESS, USE, OR ALTERATION OF YOUR TRANSMISSIONS OR CONTENT, WHETHER BASED ON WARRANTY, CONTRACT, 
              TORT (INCLUDING NEGLIGENCE) OR ANY OTHER LEGAL THEORY, WHETHER OR NOT WE HAVE BEEN INFORMED OF THE POSSIBILITY OF SUCH 
              DAMAGE, AND EVEN IF A REMEDY SET FORTH HEREIN IS FOUND TO HAVE FAILED OF ITS ESSENTIAL PURPOSE.
            </p>
            <p>
              SOME JURISDICTIONS DO NOT ALLOW THE LIMITATION OF LIABILITY FOR PERSONAL INJURY, OR OF INCIDENTAL OR CONSEQUENTIAL DAMAGES, 
              SO THIS LIMITATION MAY NOT APPLY TO YOU.
            </p>

            <h2>11. Indemnification</h2>
            <p>
              You agree to defend, indemnify, and hold harmless Meira, its parents, subsidiaries, affiliates, and their respective 
              officers, directors, employees, agents, and representatives from and against any claims, damages, obligations, losses, 
              liabilities, costs or debt, and expenses (including but not limited to attorney's fees) arising from: (i) your use of and 
              access to the Services; (ii) your violation of any term of these Terms; (iii) your violation of any third-party right, 
              including without limitation any copyright, property, or privacy right; or (iv) any claim that your User Content caused 
              damage to a third party.
            </p>

            <h2>12. Governing Law and Jurisdiction</h2>
            <p>
              These Terms and your use of the Services shall be governed by and construed in accordance with the laws of the State of 
              California, without giving effect to any principles of conflicts of law. Any legal action or proceeding relating to your 
              access to, or use of, the Services or these Terms shall be instituted in a state or federal court in San Francisco County, 
              California, and you agree to submit to the personal jurisdiction of such courts.
            </p>

            <h2>13. Dispute Resolution</h2>
            <p>
              Any dispute arising from or relating to the subject matter of these Terms shall be finally settled by arbitration in San 
              Francisco County, California, using the English language in accordance with the Arbitration Rules and Procedures of Judicial 
              Arbitration and Mediation Services, Inc. ("JAMS") then in effect, by one commercial arbitrator with substantial experience 
              in resolving intellectual property and commercial contract disputes, who shall be selected from the appropriate list of JAMS 
              arbitrators in accordance with such Rules.
            </p>
            <p>
              Any award rendered by the arbitrator shall be final and binding on the parties and may be entered as a judgment in any court 
              of competent jurisdiction. The parties acknowledge and agree that the Federal Arbitration Act and federal arbitration law apply 
              to arbitrations under these Terms. Notwithstanding the foregoing obligation to arbitrate disputes, each party shall have the 
              right to pursue injunctive or other equitable relief at any time, from any court of competent jurisdiction.
            </p>

            <h2>14. Entire Agreement</h2>
            <p>
              These Terms constitute the entire agreement between you and Meira regarding the use of the Services, superseding any prior 
              agreements between you and Meira relating to your use of the Services.
            </p>

            <h2>15. Waiver and Severability</h2>
            <p>
              The failure of Meira to enforce any right or provision of these Terms will not be deemed a waiver of such right or provision. 
              In the event that any provision of these Terms is held to be invalid or unenforceable, then that provision will be limited or 
              eliminated to the minimum extent necessary, and the remaining provisions of these Terms will remain in full force and effect.
            </p>

            <h2>16. Contact Information</h2>
            <p>
              If you have any questions about these Terms, please contact us:
            </p>
            <ul>
              <li>By email: legal@meiracorp.com</li>
              <li>By mail: Meira Legal Department, 123 Education Lane, San Francisco, CA 94107</li>
              <li>By phone: +1 (555) 123-4567</li>
            </ul>

            <p className="mt-10 text-center text-gray-500">
              By using Meira's services, you acknowledge that you have read, understood, and agree to be bound by these Terms of Service.
            </p>
          </div>
        </div>
      </div>
    </>
  );
};

export default TermsOfService;
